import { useState } from 'react'
import { Copy, Download, FileText, ImagePlus, Layers3, Trash2, Upload } from 'lucide-react'
import { COLOR_OPTIONS, formatPrice, getElementLabel, structuralWallCount } from '../data'

function InspectorNumber({ label, value, onChange, step = 0.25, suffix = 'm' }) {
  return (
    <label className="inspector-row">
      <span>{label}</span>
      <span className="inspector-input">
        <input type="number" value={Number(value).toFixed(2)} step={step} onChange={(event) => onChange(Number(event.target.value))} />
        <small>{suffix}</small>
      </span>
    </label>
  )
}

function ElementList({ elements, selectedId, onSelect, topology }) {
  const structuralWalls = structuralWallCount(topology)
  const groups = elements.reduce((acc, item) => {
    const key = item.type === 'custom' ? `custom:${item.definitionId || item.name}` : item.type
    if (!acc[key]) acc[key] = { count: 0, first: item, label: getElementLabel(item) }
    acc[key].count += 1
    return acc
  }, {})

  return (
    <section className="project-elements panel-section">
      <div className="section-title"><h3>Elementy projektu</h3><Layers3 size={17} /></div>
      <div className="project-list">
        {structuralWalls > 0 ? (
          <div className="project-list-static"><span>Ściana zabudowy</span><strong>{structuralWalls}</strong></div>
        ) : null}
        {Object.entries(groups).map(([key, group]) => {
          const selected = elements.find((item) => item.id === selectedId)
          const selectedKey = selected?.type === 'custom' ? `custom:${selected.definitionId || selected.name}` : selected?.type
          return (
            <button type="button" key={key} className={selectedKey === key ? 'active' : ''} onClick={() => onSelect(group.first.id)}>
              <span>{group.label}</span><strong>{group.count}</strong>
            </button>
          )
        })}
      </div>
    </section>
  )
}

const faceLabels = { front: 'Przód', back: 'Tył', left: 'Lewy bok', right: 'Prawy bok', top: 'Blat / góra' }

function graphicFaces(item) {
  if (item.type === 'counter') return ['front', 'back', 'left', 'right', 'top']
  if (item.type === 'wall' || item.type === 'screen') return ['front', 'back']
  if (item.type === 'custom' && item.mode !== 'suspended') {
    if (item.shape === 'box') return ['front', 'back', 'left', 'right', 'top']
    if (item.shape === 'panel') return ['front', 'back']
  }
  return []
}

function GraphicControls({ item, onUpload, onRemove }) {
  const faces = graphicFaces(item)
  const [face, setFace] = useState(faces[0] || 'front')
  if (!faces.length) return null
  const graphic = item.graphics?.[face]
  return (
    <div className="element-graphic-editor">
      <div className="element-graphic-heading"><span><ImagePlus size={15} />Grafika powierzchni</span><small>PDF / JPG / PNG</small></div>
      <label className="inspector-row graphic-face-select">
        <span>Powierzchnia</span>
        <select value={face} onChange={(event) => setFace(event.target.value)}>
          {faces.map((value) => <option value={value} key={value}>{faceLabels[value]}</option>)}
        </select>
      </label>
      <div className="element-graphic-card">
        <span className="element-graphic-preview">{graphic?.dataUrl ? <img src={graphic.dataUrl} alt="Podgląd grafiki" /> : <ImagePlus size={22} />}</span>
        <span className="element-graphic-meta"><strong>{graphic?.name || 'Brak grafiki'}</strong><small>{faceLabels[face]}</small></span>
        <label className="element-graphic-upload"><Upload size={14} />{graphic ? 'Zmień' : 'Wgraj'}<input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={(event) => onUpload(face, event)} /></label>
        {graphic ? <button type="button" className="element-graphic-remove" onClick={() => onRemove(face)} title="Usuń grafikę"><Trash2 size={14} /></button> : null}
      </div>
    </div>
  )
}

export default function RightSidebar({ selected, elements, topology, total, onUpdate, onDuplicate, onDelete, onSelect, onExportPdf, onExportElement, onUploadGraphic, onRemoveGraphic }) {
  return (
    <aside className="right-sidebar panel">
      <section className="inspector panel-section">
        <div className="section-title"><h2>Wybrany element</h2></div>
        {selected ? (
          <>
            <div className="selected-summary">
              <span className="selected-preview" style={{ '--preview-color': selected.color }} />
              <span><strong>{getElementLabel(selected)}</strong><small>ID: {selected.id.slice(-8).toUpperCase()}</small></span>
            </div>
            <div className="inspector-fields">
              <InspectorNumber label="Pozycja X" value={selected.x} onChange={(x) => onUpdate({ x })} />
              <InspectorNumber label="Pozycja Z" value={selected.z} onChange={(z) => onUpdate({ z })} />
              <InspectorNumber label="Rotacja" value={selected.rotation} onChange={(rotation) => onUpdate({ rotation })} step={5} suffix="°" />
              <InspectorNumber label="Szerokość" value={selected.width} onChange={(width) => onUpdate({ width: Math.max(0.25, width) })} />
              <InspectorNumber label="Głębokość" value={selected.depth} onChange={(depth) => onUpdate({ depth: Math.max(0.05, depth) })} />
              <InspectorNumber label="Wysokość" value={selected.height} onChange={(height) => onUpdate({ height: Math.max(0.25, height) })} />
              {selected.mode === 'suspended' ? <InspectorNumber label="Zawieszenie" value={selected.elevation} onChange={(elevation) => onUpdate({ elevation: Math.max(0, elevation) })} /> : null}
              <label className="inspector-row">
                <span>Materiał</span>
                <select value={selected.color} onChange={(event) => onUpdate({ color: event.target.value })}>
                  {COLOR_OPTIONS.map((color) => <option value={color.value} key={color.value}>{color.label}</option>)}
                </select>
              </label>
            </div>
            <GraphicControls key={selected.id} item={selected} onUpload={onUploadGraphic} onRemove={onRemoveGraphic} />
            <div className="inspector-actions">
              <button type="button" onClick={onDuplicate}><Copy size={16} />Duplikuj</button>
              <button type="button" className="danger" onClick={onDelete}><Trash2 size={16} />Usuń</button>
              <button className="export-element" type="button" onClick={onExportElement}><Download size={16} />Eksportuj element</button>
            </div>
          </>
        ) : (
          <div className="empty-selection">Kliknij element w scenie 3D, aby edytować jego parametry.</div>
        )}
      </section>

      <ElementList elements={elements} selectedId={selected?.id} onSelect={onSelect} topology={topology} />

      <section className="summary panel-section">
        <h3>Podsumowanie</h3>
        <div className="summary-price"><span>Wartość netto</span><strong>{formatPrice(total)} zł</strong></div>
        <button className="primary wide" type="button" onClick={onExportPdf}><FileText size={18} />Generuj specyfikację</button>
      </section>
    </aside>
  )
}
